import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ITheadSort, ListLayout } from '../../global/tables/table-list-layout';
import { connectZoomVM } from '../zoom.context';
import { Routes } from '../../../routes-table';
import DefaultButton from '../../global/default-button/default-button';
import { Link } from '../../../infrastructure/components/router-extensions';
import { PagingViewModel } from '../../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { Paging } from '../../global/_paging/paging-container';
import { SearchInput } from '../../global/search-input/search-input';
import { TTrackedUsersListRequestSortKeys } from '../../../api/zoom.dao';
import { NoDataFound } from '../../global/no-data';
import { SortOrder } from '../../../api/shared.dto';
import { ResourcesNavigation } from '../../_events/_manage-event/shared/navigation/navigation';
import OptionsButton from '../../global/options-button/options-button';
import Text from '../../global/text/text';
import { getNavigationLabels, SEPARATOR } from '../../../constants/navigation-labels.constants';
import { ITrackedZoomUser } from './tracked-users.viewmodel';
import { ZOOM_USER_HOST_KEY_TIMEOUT_DELAY } from '../../../constants/common.constants';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { GROUPS_TEXT, LOADING, MORE_BUTTON_TEXT } from '../../../constants/common-labels.constants';

interface IZoomUsersManagementProps {
    searchQuery: string;
    trackedUsers: ITrackedZoomUser[];
    trackedUsersLength: number;
    pagingVM: PagingViewModel;
    pageNumber: number;
    refreshAllZoomUsers: () => Promise<void>;
    refreshAllZoomUsersLoading: boolean;
    refreshHostKeys: () => Promise<void>;
    refreshHostKeysLoading: boolean;
    refreshWebinars: (intl: IntlShape, zoomUserId: string) => void;
    refreshSchedule: (intl: IntlShape, zoomUserId: string) => void;
    setSearchQuery: (query: string, needExecuteSearch?: boolean) => void;
    sortRows: (sortField: string, sortOrder: SortOrder) => void;
    sortOrder: SortOrder;
    sortField: TTrackedUsersListRequestSortKeys;
    executeSearch: () => void;
    intl?: IntlShape;
    updateZoomUserHostKey: (zoomUserId: string) => void;
}

const zoomUserHostKeyButtonsName = 'ZoomUserHostKeyButtons';

const TrackedUserRow = (props: {
    intl: IntlShape;
    user: ITrackedZoomUser;
    refreshSchedule: (intl: IntlShape, zoomUserId: string) => void;
    refreshWebinars: (intl: IntlShape, zoomUserId: string) => void;
    updateZoomUserHostKey: (zoomUserId: string) => void;
}) => {

    const [disabled, setDisabled] = useState<boolean>(false);

    const storedZoomUserHostKeyButtons: [{ zoomUserId: string; date: string }] = JSON.parse(localStorage.getItem(zoomUserHostKeyButtonsName)) || [];

    const getTimeDifference = (date: string): number => {
        return (new Date().getTime() - new Date(date).getTime());
    };

    const clearStoredZoomUserHostKeyButtons = (): void => {
        const storedButtons = storedZoomUserHostKeyButtons.filter(user => {
            if (getTimeDifference(user.date) < ZOOM_USER_HOST_KEY_TIMEOUT_DELAY) {
                return user;
            }
        });
        localStorage.setItem(zoomUserHostKeyButtonsName, JSON.stringify(storedButtons));
    };

    useEffect(() => {
        let differenceTime = null;
        storedZoomUserHostKeyButtons.find(user => {
            if (user.zoomUserId === props.user.zoomId) {
                differenceTime = getTimeDifference(user.date);
            }
        });
        if (differenceTime) {
            if (differenceTime > ZOOM_USER_HOST_KEY_TIMEOUT_DELAY) {
                setDisabled(false);
            } else {
                setDisabled(true);
                const timeoutId = setTimeout(() => {
                    setDisabled(false);
                    clearTimeout(timeoutId);
                }, ZOOM_USER_HOST_KEY_TIMEOUT_DELAY - differenceTime);
            }
        }
        clearStoredZoomUserHostKeyButtons();
    }, []);

    useEffect(() => {
        return () => {
            clearStoredZoomUserHostKeyButtons();
        };
    }, []);

    const refreshHostKey = (): void => {
        props.updateZoomUserHostKey(props.user.zoomId);
        storedZoomUserHostKeyButtons.push({ zoomUserId: props.user.zoomId, date: new Date().toString() });
        localStorage.setItem(zoomUserHostKeyButtonsName, JSON.stringify(storedZoomUserHostKeyButtons));
        setDisabled(true);
        const timeoutId = setTimeout(() => {
            setDisabled(false);
            clearTimeout(timeoutId);
        }, ZOOM_USER_HOST_KEY_TIMEOUT_DELAY);
    };

    return (
        <tr className='tracked-user-row'>
            <td className='hoverable overflow-ellipsis' >{props.user.email}</td>
            <Text className='hoverable' tag='td' message={props.user.firstName} withEllipsis />
            <Text className='hoverable' tag='td' message={props.user.lastName} withEllipsis />
            <td className='hoverable'>{props.user.groups ? props.user.groups.map(e => e.name).join(', ') : ''}</td>
            <td className='hoverable'>{props.user.hostKey}</td>
            <td className='hoverable align-right'>
                {hasPermission(Permission.REFRESH_HOST_KEYS) &&
                    <DefaultButton
                        className='visible-while-hovered'
                        modifier='slim'
                        text={
                            <FormattedMessage
                                id='oe.zoom.refreshHostKey'
                                defaultMessage='Refresh Host Key'
                                description='Label text'
                            />
                        }
                        onClick={() => refreshHostKey()}
                        disabled={disabled}
                    />
                }
            </td>
            <td className='hoverable align-right'>
                <DefaultButton
                    className='visible-while-hovered'
                    modifier='slim'
                    text={
                        <FormattedMessage
                            id='oe.zoom.refreshWebinars'
                            defaultMessage='Refresh Webinars'
                            description='Label text'
                        />
                    }
                    onClick={() => props.refreshWebinars(props.intl, props.user.zoomId)}
                />
            </td>
            <td className='hoverable align-right'>
                <DefaultButton
                    className='visible-while-hovered'
                    modifier='slim'
                    text={
                        <FormattedMessage
                            id='oe.meetings.refresh'
                            defaultMessage='Refresh Meetings'
                            description='Label text'
                        />
                    }
                    onClick={() => props.refreshSchedule(props.intl, props.user.zoomId)}
                />
            </td>
        </tr>
    );
};

const TrackedUsersListDisconnected = (props: IZoomUsersManagementProps) => {
    const { trackedUsers, intl } = props;
    const labels = getNavigationLabels(intl);

    const thead: ITheadSort<TTrackedUsersListRequestSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: `oe.common.email`,
                defaultMessage: 'Email',
                description: 'Label text',
            }), sortField: 'email', width: '30%'
        },
        {
            title: intl.formatMessage({
                id: `oe.common.firstName`,
                defaultMessage: 'First Name',
                description: 'Label text',
            }), sortField: 'firstName', width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.common.lastName`,
                defaultMessage: 'Last Name',
                description: 'Label text',
            }), sortField: 'lastName', width: '20%'
        },
        {
            title: intl.formatMessage(GROUPS_TEXT),
            width: '20%'
        },
        {
            title: intl.formatMessage({
                id: `oe.common.hostKey`,
                defaultMessage: 'Host Key',
                description: 'Label text',
            }), width: '15%'
        },
        { width: '160px' },
        { width: '160px' },
        {
            customNode:
                <div className={'search-input-container'}>
                    <SearchInput
                        resetSearch={() => props.setSearchQuery('', true)}
                        value={props.searchQuery}
                        onChange={e => props.setSearchQuery(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                props.executeSearch();
                            }
                        }}
                        withResetSearchButton
                    />
                </div>,
            width: '160px'
        },
    ];

    const showUsersList = !!(trackedUsers && trackedUsers.length);
    const showFetchingUsers = !trackedUsers;
    const showNoUsers = trackedUsers && !trackedUsers.length;
    const COL_SPAN = thead.length;

    const buttonOptions = [
        {
            label: intl.formatMessage({
                id: `oe.zoom.refreshAllMeetings`,
                defaultMessage: 'Refresh All Zoom Meetings',
                description: 'Label text',
            }),
            action: props.refreshAllZoomUsers,
            disabled: props.refreshAllZoomUsersLoading
        },
        hasPermission(Permission.REFRESH_HOST_KEYS) && {
            label: intl.formatMessage({
                id: `oe.zoom.refreshHostKeys`,
                defaultMessage: 'Refresh Host Keys',
                description: 'Label text',
            }),
            action: props.refreshHostKeys,
            disabled: props.refreshHostKeysLoading
        },
    ].filter(Boolean);

    useEffect(() => {
        document.title = `${labels.ROOT} ${SEPARATOR} ${labels.RESOURCES_LIST} ${SEPARATOR} ${labels.ZOOM_NAVIGATION.USERS}`;
    }, []);

    return (
        <div className='tracked-users-list'>
            <section className='resources-tabs horizontal-row horizontal-row--with-bottom-margin'>
                <ResourcesNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        <Link routeEnum={Routes.ZoomUsersManagement}>
                            <DefaultButton
                                text={
                                    <FormattedMessage
                                        id='oe.users.manageZoomUser'
                                        defaultMessage='Manage Zoom Users'
                                        description='Button text'
                                    />
                                }
                                modifier={['fat', 'confirm-safe']}
                            />
                        </Link>
                        <OptionsButton
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={buttonOptions}
                        />
                    </div>
                </div>
            </section>
            <section className='tracked-users-list fixed-list-layout'>
                <ListLayout sortOptions={{
                    theads: thead,
                    sortRows: props.sortRows,
                    sortField: props.sortField,
                    sortOrder: props.sortOrder,
                }}>
                    {showUsersList && trackedUsers.map(x => <TrackedUserRow {...{
                        intl: intl,
                        user: x,
                        refreshSchedule: props.refreshSchedule,
                        refreshWebinars: props.refreshWebinars,
                        updateZoomUserHostKey: props.updateZoomUserHostKey
                    }} key={x.zoomId} />)}
                    {showFetchingUsers && <tr><td className='table-loading' colSpan={COL_SPAN}>
                        <FormattedMessage
                            {...LOADING}
                            values={{
                                suffix: '...'
                            }}
                        />
                    </td></tr>}
                    {showNoUsers && <tr>
                        <td className='table-loading' colSpan={COL_SPAN}>
                            <NoDataFound
                                imageDescriptionText={
                                    <FormattedMessage
                                        id='oe.zoom.noUsersFound'
                                        defaultMessage='No Zoom users found.'
                                        description='Label text'
                                    />
                                } />
                        </td>
                    </tr>}
                </ListLayout>
            </section>
            <Paging pagingVM={props.pagingVM} />
        </div>
    );
};

const TrackedUsersList = injectIntl(connectZoomVM<IZoomUsersManagementProps>(TrackedUsersListDisconnected, zoomVM => {
    const trackedUserVM = zoomVM.getTrackedUserVM();
    trackedUserVM.initialize();
    const trackedUsers = trackedUserVM.getTrackedUsers();
    const pagingVM = trackedUserVM.getPagingVM();
    const refreshAllZoomUsersLoading = trackedUserVM.getRefreshAllZoomUsersLoading();
    const refreshHostKeysLoading = trackedUserVM.getRefreshHostKeysLoading();
    return {
        pagingVM: pagingVM,
        trackedUsers: trackedUsers,
        trackedUsersLength: trackedUsers && trackedUsers.length,
        pageNumber: pagingVM.getCurrentPage(),
        refreshAllZoomUsers: () => trackedUserVM.refreshAllZoomUsers(),
        refreshAllZoomUsersLoading: refreshAllZoomUsersLoading,
        refreshHostKeys: () => trackedUserVM.refreshHostKeys(),
        refreshHostKeysLoading: refreshHostKeysLoading,
        refreshWebinars: (intl, zoomUserId) => trackedUserVM.refreshWebinars(intl, zoomUserId),
        refreshSchedule: (intl, zoomUserId) => trackedUserVM.refreshUserMeetingsSchedule(intl, zoomUserId),
        searchQuery: trackedUserVM.getSearchQuery(),
        setSearchQuery: trackedUserVM.setSearchQuery,
        sortRows: trackedUserVM.sortTableRows.bind(trackedUserVM),
        sortField: trackedUserVM.getSortField(),
        sortOrder: trackedUserVM.getSortOrder(),
        executeSearch: trackedUserVM.executeSearch,
        updateZoomUserHostKey: zoomUserId => trackedUserVM.updateZoomUserHostKey(zoomUserId),
    };
}));

export { TrackedUsersList, TrackedUserRow };
